import './styles/main.css';
import { initMotion } from './motion.js';
import { initSmoothScroll } from './smooth-scroll.js';
import { cases, offers, journey, toolkit, marquee, faqs, testimonials } from './content/projects.js';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/** Minimal escaping — content is ours, but templates should not be able to
 *  inject markup by accident. */
const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* --------------------------------------------------------------- contact */

/*
 * Contact details are assembled at runtime from parts rather than sitting in
 * the HTML as plain text. Address-harvesting bots overwhelmingly scrape static
 * markup and do not execute modules, so this removes the easy hit while
 * remaining fully functional for humans and screen readers.
 */
const CONTACT = {
  emailUser: 'sooryaprakash.pl',
  emailDomain: 'gmail.com',
  phoneCC: '91',
  phoneRest: '7845562628',
};

const email = () => `${CONTACT.emailUser}@${CONTACT.emailDomain}`;
const phoneIntl = () => `+${CONTACT.phoneCC} ${CONTACT.phoneRest.slice(0, 5)} ${CONTACT.phoneRest.slice(5)}`;
const phoneDigits = () => `${CONTACT.phoneCC}${CONTACT.phoneRest}`;

/*
 * Social profiles. The owner has not supplied URLs yet, so these render as
 * visibly inert chips rather than links to nowhere — a dead link is worse than
 * an obviously unfinished one. Fill `url` in and they become real links.
 */
const SOCIALS = [
  { name: 'GitHub', url: null },
  { name: 'LinkedIn', url: null },
  { name: 'Instagram', url: null },
];

/* --------------------------------------------------------------- render */

function renderMarquee() {
  const host = $('[data-marquee]');
  if (!host) return;
  const run = marquee.map((m) => `<span>${esc(m)}</span><b>◆</b>`).join('');
  // Duplicated so the -50% translate loops seamlessly.
  host.innerHTML = run + run;
}

function renderCases() {
  const host = $('[data-cases]');
  if (!host) return;
  host.innerHTML = cases
    .map(
      (c) => `<article class="case" data-case data-reveal>
        <span class="case__wipe" data-wipe aria-hidden="true"></span>
        <div class="case__num" data-num>${esc(c.num)}</div>
        <div>
          <h3 class="case__title">${esc(c.title)}</h3>
          <p class="case__meta">${esc(c.meta)}</p>
          <p class="case__body">${esc(c.body)}</p>
          <div class="case__tags">${c.tags.map((t) => `<span>${esc(t)}</span>`).join('')}</div>
        </div>
        <div class="case__metrics">
          ${c.metrics
            .map(
              (m) => `<div>
                <span class="case__metric-num">${esc(m.value)}</span>
                <span class="case__metric-label">${esc(m.label)}</span>
              </div>`
            )
            .join('')}
        </div>
      </article>`
    )
    .join('');
}

function renderOffers() {
  const host = $('[data-offers]');
  if (!host) return;
  host.innerHTML = offers
    .map(
      (o) => `<div class="offer" data-reveal>
        <div class="offer__num">${esc(o.num)}</div>
        <h3>${esc(o.title)}</h3>
        <p>${esc(o.body)}</p>
      </div>`
    )
    .join('');
}

function renderJourney() {
  const host = $('[data-journey]');
  if (!host) return;
  host.innerHTML = journey
    .map(
      (j) => `<div class="jrn" data-reveal data-accent="${esc(j.accent)}">
        <div class="jrn__year">${esc(j.year)}</div>
        <div class="jrn__node" data-node aria-hidden="true"></div>
        <h3>${esc(j.title)}</h3>
        <p>${esc(j.body)}</p>
        ${j.draft ? '<p class="jrn__draft">Draft — Soorya to confirm</p>' : ''}
      </div>`
    )
    .join('');
}

function renderToolkit() {
  const host = $('[data-toolkit]');
  if (!host) return;
  host.innerHTML = toolkit
    .map(
      (g) => `<div data-reveal>
        <div class="tool__head">${esc(g.group)}</div>
        <div class="tool__list">${g.items.map((i) => `<span>${esc(i)}</span>`).join('')}</div>
      </div>`
    )
    .join('');
}

function renderContact() {
  const host = $('[data-contact-methods]');
  if (!host) return;

  const methods = [
    { label: 'Email', value: email(), href: `mailto:${email()}` },
    { label: 'WhatsApp', value: phoneIntl(), href: `https://wa.me/${phoneDigits()}` },
    { label: 'Phone', value: phoneIntl(), href: `tel:+${phoneDigits()}` },
    { label: 'Based in', value: 'Coimbatore & Tiruchengode', href: null },
  ];

  host.innerHTML = methods
    .map((m) => {
      const inner =
        `<span class="contact-method__label">${esc(m.label)}</span>` +
        `<span class="contact-method__value">${esc(m.value)}</span>`;
      return m.href
        ? `<a class="contact-method" href="${esc(m.href)}"${m.href.startsWith('http') ? ' target="_blank" rel="noopener"' : ''}>${inner}</a>`
        : `<div class="contact-method">${inner}</div>`;
    })
    .join('');
}

function renderSocials() {
  const host = $('[data-socials]');
  if (!host) return;
  host.innerHTML = SOCIALS.map((s) =>
    s.url
      ? `<a class="social" href="${esc(s.url)}" target="_blank" rel="noopener me">${esc(s.name)}</a>`
      : `<span class="social" data-pending="true" title="Link coming soon">${esc(s.name)}</span>`
  ).join('');
}

/**
 * FAQ accordion.
 *
 * Built from <button> + a region rather than <details>, so the open/close can
 * be animated on height and the state exposed via aria-expanded. One panel open
 * at a time keeps the two columns from jumping around.
 */
function renderFaq() {
  const host = $('[data-faq]');
  if (!host) return;

  host.innerHTML = faqs
    .map(
      (f, i) => `<div class="faq__item" data-reveal>
        <button type="button" class="faq__q" aria-expanded="false" aria-controls="faq-a-${i}" id="faq-q-${i}">
          <span>${esc(f.q)}</span>
          <span class="faq__sign" aria-hidden="true"></span>
        </button>
        <div class="faq__a" id="faq-a-${i}" role="region" aria-labelledby="faq-q-${i}" hidden>
          <p>${esc(f.a)}</p>
        </div>
      </div>`
    )
    .join('');

  $$('.faq__q', host).forEach((btn) => {
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      // Close the others so the columns keep a stable height.
      $$('.faq__q', host).forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(b.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
      if (!open) {
        btn.setAttribute('aria-expanded', 'true');
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = false;
      }
    });
  });
}

/**
 * Testimonial carousel.
 *
 * The whole section stays hidden while `testimonials` is empty — Soorya has no
 * real quotes yet, and an empty carousel or invented praise would both be worse
 * than simply not having the section. It appears automatically once quotes are
 * added to the content file.
 *
 * Drag with the pointer, or arrow keys when the region has focus.
 */
function renderQuotes() {
  const section = $('[data-quotes-section]');
  const viewport = $('[data-quotes]');
  const track = $('[data-quotes-track]');
  const bar = $('[data-quotes-progress]');
  if (!section || !viewport || !track) return;

  if (!testimonials.length) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  track.innerHTML = testimonials
    .map(
      (t) => `<article class="quote">
        <p class="quote__text">${esc(t.quote)}</p>
        <footer class="quote__by">
          <strong>${esc(t.name)}</strong>
          <span>${esc(t.role)}${t.org ? ` · ${esc(t.org)}` : ''}</span>
        </footer>
      </article>`
    )
    .join('');

  let down = false;
  let startX = 0;
  let startLeft = 0;

  const syncBar = () => {
    if (!bar) return;
    const max = track.scrollWidth - viewport.clientWidth;
    bar.style.transform = `scaleX(${max > 0 ? viewport.scrollLeft / max : 1})`;
  };

  viewport.addEventListener('pointerdown', (e) => {
    down = true;
    startX = e.clientX;
    startLeft = viewport.scrollLeft;
    viewport.setPointerCapture(e.pointerId);
    viewport.dataset.dragging = 'true';
  });
  viewport.addEventListener('pointermove', (e) => {
    if (!down) return;
    viewport.scrollLeft = startLeft - (e.clientX - startX);
  });
  const end = () => {
    down = false;
    delete viewport.dataset.dragging;
  };
  viewport.addEventListener('pointerup', end);
  viewport.addEventListener('pointercancel', end);
  viewport.addEventListener('scroll', syncBar, { passive: true });

  // Keyboard equivalent — a drag-only control is unusable without a mouse.
  viewport.addEventListener('keydown', (e) => {
    const step = viewport.clientWidth * 0.8;
    if (e.key === 'ArrowRight') { viewport.scrollLeft += step; e.preventDefault(); }
    if (e.key === 'ArrowLeft') { viewport.scrollLeft -= step; e.preventDefault(); }
  });

  syncBar();
}

/**
 * Inline chips: hover or focus expands one into a card.
 *
 * Driven from JS so `aria-expanded` reflects the visual state — a CSS-only
 * :hover version says nothing to a screen reader and cannot be reached by
 * keyboard at all.
 */
function initChips() {
  $$('[data-chip]').forEach((chip) => {
    const btn = chip.querySelector('.chip__btn');
    if (!btn) return;
    const open = (on) => {
      chip.dataset.open = String(on);
      btn.setAttribute('aria-expanded', String(on));
    };
    chip.addEventListener('mouseenter', () => open(true));
    chip.addEventListener('mouseleave', () => open(false));
    btn.addEventListener('focus', () => open(true));
    btn.addEventListener('blur', () => open(false));
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      open(chip.dataset.open !== 'true');
    });
  });
}

/* ----------------------------------------------------------- behaviour */

/** Sidebar collapse under 1000px. Uses a data attribute rather than `hidden`
 *  so the desktop layout is untouched. */
function initSidebar() {
  const sidebar = $('[data-sidebar]');
  const toggle = $('[data-nav-toggle]');
  if (!sidebar || !toggle) return;

  const setOpen = (open) => {
    toggle.setAttribute('aria-expanded', String(open));
    sidebar.dataset.open = String(open);
  };

  toggle.addEventListener('click', () => setOpen(sidebar.dataset.open !== 'true'));
  sidebar.addEventListener('click', (e) => {
    if (e.target.closest('a')) setOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setOpen(false);
  });
}

/* ----------------------------------------------------------------- boot */

function boot() {
  renderMarquee();
  renderCases();
  renderOffers();
  renderJourney();
  renderToolkit();
  renderFaq();
  renderQuotes();
  renderContact();
  renderSocials();

  const yearEl = $('[data-year]');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());

  initSidebar();
  initChips();
  // Motion must run after render so it can see the injected elements.
  initMotion();
  initSmoothScroll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
